import { APIClientInterface } from '../types'
import { getCookie } from './manageCookies'
import RequestError from './RequestError'

type HeaderOptions = { key: string; value: string }[]

export default class APIClient implements APIClientInterface {
	private apiUrl = `${this.baseUrl}/api/v1`

	constructor(private baseUrl: string) {}

	private get baseHeaders(): Headers {
		return new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' })
	}

	public async get<T>(url: string, headers?: HeaderOptions, abortSignal?: AbortSignal): Promise<T> {
		const requestHeaders = this.baseHeaders
		if (headers) {
			headers.forEach(header => requestHeaders.append(header.key, header.value))
		}

		const response = await fetch(`${this.apiUrl}${url}`, {
			headers: requestHeaders,
			credentials: 'include',
			signal: abortSignal
		})

		const json = await response.json()
		if (!response.ok) {
			throw new RequestError(`${response.status}`, { status: response.status, body: json })
		}

		return json
	}

	public async delete<T>(url: string, headers?: HeaderOptions): Promise<T> {
		const requestHeaders = this.baseHeaders
		requestHeaders.append('X-XSRF-TOKEN', window.decodeURIComponent(getCookie('XSRF-TOKEN')))
		if (headers) {
			headers.forEach(header => requestHeaders.append(header.key, header.value))
		}

		const response = await fetch(`${this.apiUrl}${url}`, { method: 'delete', headers: requestHeaders, credentials: 'include' })
		if (response.status === 204) {
			return Promise.resolve(null)
		}

		const json = await response.json()
		if (!response.ok) {
			throw new RequestError(`${response.status}`, { status: response.status, body: json })
		}

		return json
	}

	public async post<T>(url: string, data: string, headers?: HeaderOptions): Promise<T | null> {
		const requestHeaders = this.baseHeaders
		requestHeaders.append('X-XSRF-TOKEN', window.decodeURIComponent(getCookie('XSRF-TOKEN')))
		if (headers) {
			headers.forEach(header => requestHeaders.append(header.key, header.value))
		}

		const response = await fetch(`${this.apiUrl}${url}`, { method: 'post', body: data, headers: requestHeaders, credentials: 'include' })
		if (response.status === 204) {
			return Promise.resolve(null)
		}

		const json = await response.json()
		if (!response.ok) {
			throw new RequestError(`${response.status}`, { status: response.status, body: json })
		}

		return json
	}

	public async postFile<T>(url: string, file: File, headers?: HeaderOptions): Promise<T> {
		const requestHeaders = new Headers({ 'X-XSRF-TOKEN': window.decodeURIComponent(getCookie('XSRF-TOKEN')) })
		if (headers) {
			headers.forEach(header => requestHeaders.append(header.key, header.value))
		}

		const formData = new FormData()
		formData.append('file', file)

		const response = await fetch(`${this.apiUrl}${url}`, { method: 'post', body: formData, headers: requestHeaders, credentials: 'include' })
		if (response.status === 204) {
			return Promise.resolve(null)
		}

		const json = await response.json()
		if (!response.ok) {
			throw new RequestError(`${response.status}`, { status: response.status, body: json })
		}

		return json
	}

	public async patch<T>(url: string, data: string, headers?: HeaderOptions): Promise<T> {
		const requestHeaders = this.baseHeaders
		requestHeaders.append('X-XSRF-TOKEN', window.decodeURIComponent(getCookie('XSRF-TOKEN')))
		if (headers) {
			headers.forEach(header => requestHeaders.append(header.key, header.value))
		}

		const response = await fetch(`${this.apiUrl}${url}`, { method: 'PATCH', body: data, headers: requestHeaders, credentials: 'include' })
		if (response.status === 204) {
			return Promise.resolve(null)
		}

		const json = await response.json()
		if (!response.ok) {
			throw new RequestError(`${response.status}`, { status: response.status, body: json })
		}

		return json
	}
}
